import type { LiveRole } from "@/lib/api/live-api";
import { recitalPath } from "./recital-routes";

/**
 * Une sortie de séance : ce qu'on dit, et où l'on va.
 *
 * `notice` est `null` quand la page d'arrivée dit mieux que nous ce qui s'est
 * passé ; `to` est `null` quand il n'y a nulle part où aller — on reste là où
 * l'on est, débarrassé de la salle.
 */
export type SessionExit = {
  notice: string | null;
  to: string | null;
};

/**
 * La salle a disparu sous nos pieds : que fait-on de la personne.
 *
 * # Quand ça arrive
 *
 * L'interprète qui arrête son live fait supprimer la salle, et LiveKit
 * déconnecte chacun là où il est : c'est ainsi que le public apprend que c'est
 * fini, sans qu'aucun message n'ait eu à le lui dire. Une liaison définitivement
 * perdue arrive par le même chemin, et le traitement est le même — dans les deux
 * cas il n'y a plus de séance à suivre, et rester dedans ne montrerait qu'un
 * rouleau qui ne bouge plus.
 *
 * # Le public repart par la salle d'attente
 *
 * C'est elle qui sait dire ce qui s'est passé : « Terminé », le replay quand il
 * est prêt, et la porte qui se rouvre si c'était le réseau et non la fin. Un
 * spectateur laissé sur un studio vide, lui, n'aurait qu'un panneau replié et
 * rien à lire. C'est pour ça qu'il n'emporte aucun message : il en trouvera un
 * meilleur en arrivant.
 *
 * L'interprète reste où il est. Son studio est le sien — son morceau est
 * ouvert, son clavier branché — et l'envoyer sur la page de son propre récital
 * lui prendrait sa place de travail pour lui apprendre ce qu'il vient de faire.
 */
export function exitOnDrop(role: LiveRole | null, recitalId: string | null): SessionExit {
  if (role === "listener" && recitalId !== null) {
    return { notice: null, to: recitalPath(recitalId) };
  }

  return { notice: recitalId === null ? "La séance est terminée." : "Le récital est terminé.", to: null };
}
